import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Header = ({ activeSection, setActiveSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Close mobile menu first
    setIsMobileMenuOpen(false);
    
    // Small delay to ensure menu closes before scrolling
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(sectionId);
      }
    }, 300);
  };

  const menuItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'Sobre' },
    { id: 'services', label: 'Serviços' },
    { id: 'team', label: 'Equipe' },
    { id: 'portfolio', label: 'Portfólio' },
    { id: 'contact', label: 'Contato' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-card bg-opacity-95' : 'bg-transparent'
      }`}
      style={{
        background: isScrolled ? 'rgba(26, 28, 27, 0.95)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(16px)' : 'none'
      }}
    >
      <div className="container">
        <nav className="flex items-center justify-between py-4">
          {/* Logo */}
          <div className="flex items-center" onClick={() => scrollToSection('home')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_b643e2b7-1d18-42a1-b855-27b1c43cee32/artifacts/6v4v1pgk_compacta.svg"
              alt="Manch Studios" 
              className="h-8 sm:h-10 w-auto cursor-pointer filter invert transition-all duration-300 hover:opacity-80"
            />
          </div>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollToSection(item.id)}
                  className={`nav-link ${
                    activeSection === item.id ? 'active' : ''
                  }`}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            {/* Full Screen Menu - Optimized */}
            <div className="fixed inset-0 z-[999] bg-bg-page/95 backdrop-blur-xl">
              {/* Overlay Click to Close */}
              <div 
                className="absolute inset-0" 
                onClick={() => setIsMobileMenuOpen(false)}
              ></div>
              
              {/* Menu Content */}
              <div className="relative z-10 h-full flex flex-col">
                {/* Menu Header */}
                <div className="flex items-center justify-between p-8 pt-16">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_b643e2b7-1d18-42a1-b855-27b1c43cee32/artifacts/6v4v1pgk_compacta.svg"
                      alt="Manch Studios" 
                      className="h-10 w-auto filter invert"
                    />
                    <div className="w-px h-8 bg-brand-primary/40"></div>
                    <span className="text-brand-primary text-sm font-medium tracking-widest">MENU</span>
                  </div>
                  
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:border-brand-primary/50 transition-colors duration-200"
                    aria-label="Fechar menu"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                {/* Menu Items - Simplified */}
                <div className="flex-1 flex flex-col items-center justify-center px-8">
                  <div className="w-full max-w-sm space-y-4">
                    {menuItems.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full rounded-xl border p-5 transition-all duration-200 ${
                          activeSection === item.id 
                            ? 'bg-brand-primary border-brand-primary text-text-inverse' 
                            : 'bg-white/5 border-white/10 text-white hover:border-brand-primary/30 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-lg">
                            {item.label}
                          </span>
                          
                          <div className={`w-2 h-2 rounded-full ${
                            activeSection === item.id 
                              ? 'bg-text-inverse' 
                              : 'bg-brand-primary opacity-0 group-hover:opacity-100'
                          }`}></div>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Simple Decorative Line */}
                  <div className="mt-12 w-16 h-0.5 bg-brand-primary rounded-full"></div>
                </div>

                {/* Bottom Section */}
                <div className="p-8 pb-12 text-center">
                  <p className="text-white/60 text-sm">
                    Consultoria em Design de 
                    <span className="text-brand-primary font-medium"> Excelência</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;